import { useCallback, useState } from "react";

import { downloadBlob } from "@/shared/utils/downloadBlob";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";
import { corridorValue } from "../utils/batchSetupSchema";

export interface UseTemplateDownloadResult {
  download: (country: string, rail: string) => Promise<void>;
  isDownloading: boolean;
  error: BatchError | null;
  /** `ISO3:RAIL` of the last template that reached the browser, so the setup can mark that step done. */
  downloaded: string | null;
}

/** `GET /template?country&rail` → the browser's download tray. */
export function useTemplateDownload(): UseTemplateDownloadResult {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<BatchError | null>(null);
  const [downloaded, setDownloaded] = useState<string | null>(null);

  const download = useCallback(async (country: string, rail: string) => {
    setIsDownloading(true);
    setError(null);
    try {
      const file = await PayoutBatchesService.downloadTemplate(country, rail);
      downloadBlob(file.blob, file.filename);
      setDownloaded(corridorValue(country, rail));
    } catch (err: unknown) {
      setError(classifyBatchError(err));
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return { download, isDownloading, error, downloaded };
}
